import React, { useEffect, useMemo, useState } from "react";

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input as ShadcnInput } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { CustomCsvImportCapabilities } from "../../../services/brokerIntegrationApi";
import type { CustomCsvWizardState, ValueTransformMap } from "../../../types/customCsvImport";
import {
  DATETIME_FORMAT_CUSTOM_VALUE,
  buildDatetimeFormatSelectOptions,
  describeOpenCloseColumnIssue,
  distinctColumnValues,
  findImportModeOption,
  invalidatePreviewAfterConfigChange,
  isTypedTransform,
  partitionOpenCloseColumnValues,
  readCsvSampleRowsFromFile,
  validateDatetimeFormat,
} from "../../../utils/customCsvImport";
import { findLayoutShape } from "../../../utils/layoutShapes";
import {
  datetimeFormatConflictsWithSamples,
  inferDatetimeFormatFromSamples,
  labelForDatetimeFormat,
} from "../../../utils/datetimeFormatInference";
import {
  CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS,
  CUSTOM_CSV_FIELD_CARD_CLASS,
  CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS,
  CUSTOM_CSV_SELECT_CONTENT_PROPS,
} from "./customCsvWizardUi";
import CsvValueMappingTable from "./visuals/CsvValueMappingTable";
import { FILE_ORDER_PAIRING_EXPLANATION, POSITION_EFFECT_OPTIONS, SIDE_TARGET_OPTIONS } from "./customCsvImportConstants";

export interface CustomCsvValueTransformsStepProps {
  csvImport: CustomCsvImportCapabilities;
  state: CustomCsvWizardState;
  onStateChange: (patch: Partial<CustomCsvWizardState>) => void;
}

function getValueMap(
  transforms: CustomCsvWizardState["valueTransforms"],
  key: string
): ValueTransformMap {
  const raw = transforms[key];
  if (raw && !isTypedTransform(raw) && typeof raw === "object") {
    return { ...(raw as ValueTransformMap) };
  }
  return {};
}

function MappingSectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Card
      data-custom-csv-value-mapping-card=""
      className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
    >
      <CardContent className={CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS}>
        <Field className="gap-2" data-broker-csv-field="">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{title}</FieldLabel>
          {description ? (
            <FieldDescription className={BROKER_ALERT_DESC_CLASS}>{description}</FieldDescription>
          ) : null}
          <div className="pt-1">{children}</div>
        </Field>
      </CardContent>
    </Card>
  );
}

const CustomCsvValueTransformsStep = ({
  csvImport,
  state,
  onStateChange,
}: CustomCsvValueTransformsStepProps): React.ReactElement => {
  const layoutShape = findLayoutShape(csvImport, state.layoutShapeId);
  const modeOption = findImportModeOption(csvImport.import_modes, state.importMode);
  const previewSampleRows = state.preview?.sample_rows ?? [];
  const [fileSampleRows, setFileSampleRows] = useState<Record<string, string>[]>([]);

  useEffect(() => {
    if (!state.csvFile) {
      setFileSampleRows([]);
      return;
    }
    let cancelled = false;
    readCsvSampleRowsFromFile(state.csvFile, 400).then((rows) => {
      if (!cancelled) setFileSampleRows(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [state.csvFile]);

  const valueSourceRows =
    fileSampleRows.length > 0 ? fileSampleRows : previewSampleRows;
  const valuesFromFullFile = fileSampleRows.length > 0;

  const sideColumn = state.columnMapping.side;
  const timestampColumn = state.columnMapping.timestamp;
  const effectColumn = state.columnMapping.position_effect;
  const rowKindColumn = state.columnMapping.row_kind;

  const timestampSamples = useMemo(() => {
    const headers = [
      timestampColumn,
      state.columnMapping.entry_time,
      state.columnMapping.exit_time,
    ].filter((h): h is string => Boolean(h?.trim()));
    const seen = new Set<string>();
    const values: string[] = [];
    for (const header of headers) {
      for (const value of distinctColumnValues(valueSourceRows, header, 50)) {
        if (seen.has(value)) continue;
        seen.add(value);
        values.push(value);
        if (values.length >= 50) return values;
      }
    }
    return values;
  }, [
    valueSourceRows,
    timestampColumn,
    state.columnMapping.entry_time,
    state.columnMapping.exit_time,
  ]);
  const datetimeFormatOptions = useMemo(
    () => buildDatetimeFormatSelectOptions(csvImport),
    [csvImport]
  );
  const datetimeFormat = String(state.valueTransforms.datetime_format ?? "").trim();
  const inferredDatetime = useMemo(
    () => inferDatetimeFormatFromSamples(timestampSamples),
    [timestampSamples]
  );
  const datetimeFormatConflict = useMemo(
    () => datetimeFormatConflictsWithSamples(datetimeFormat, timestampSamples),
    [datetimeFormat, timestampSamples]
  );

  const sideValues = useMemo(
    () => (sideColumn ? distinctColumnValues(valueSourceRows, sideColumn) : []),
    [valueSourceRows, sideColumn]
  );
  const effectValues = useMemo(
    () => (effectColumn ? distinctColumnValues(valueSourceRows, effectColumn) : []),
    [valueSourceRows, effectColumn]
  );
  const effectValuePartition = useMemo(
    () =>
      partitionOpenCloseColumnValues(
        effectValues,
        getValueMap(state.valueTransforms, "position_effect")
      ),
    [effectValues, state.valueTransforms]
  );
  const effectColumnIssue = useMemo(
    () => (effectColumn ? describeOpenCloseColumnIssue(effectColumn) : null),
    [effectColumn]
  );
  const rowKindValues = useMemo(
    () => (rowKindColumn ? distinctColumnValues(valueSourceRows, rowKindColumn) : []),
    [valueSourceRows, rowKindColumn]
  );
  const rowKindValuePartition = useMemo(
    () =>
      partitionOpenCloseColumnValues(
        rowKindValues,
        getValueMap(state.valueTransforms, "row_kind")
      ),
    [rowKindValues, state.valueTransforms]
  );

  const datetimeFormatKnown = datetimeFormatOptions.some((o) => o.value === datetimeFormat);
  const datetimeFormatIssue = validateDatetimeFormat(datetimeFormat);
  const [forceCustomDatetimeFormat, setForceCustomDatetimeFormat] = useState(
    () => datetimeFormat !== "" && !datetimeFormatKnown
  );

  useEffect(() => {
    if (datetimeFormat !== "" && !datetimeFormatKnown) {
      setForceCustomDatetimeFormat(true);
    }
  }, [datetimeFormat, datetimeFormatKnown]);

  const applyDetectedDatetimeFormat = () => {
    const detected = datetimeFormatConflict ?? inferredDatetime;
    if (!detected) return;
    const inPresets = datetimeFormatOptions.some((o) => o.value === detected.format);
    setForceCustomDatetimeFormat(!inPresets);
    updateTransforms({ datetime_format: detected.format });
  };

  const showCustomDatetimeFormat =
    forceCustomDatetimeFormat || (datetimeFormat !== "" && !datetimeFormatKnown);

  const updateTransforms = (patch: Partial<CustomCsvWizardState["valueTransforms"]>) => {
    onStateChange({
      valueTransforms: { ...state.valueTransforms, ...patch },
      preview: invalidatePreviewAfterConfigChange(state.preview),
      acknowledgedWarningCodes: new Set(),
    });
  };

  const datetimeFormatDescription = datetimeFormatIssue
    ? datetimeFormatIssue
    : `Pick the layout that matches your file. Continue is available after you choose one. Use Other only if none of the presets fit.${
        csvImport.datetime_format_options?.length || csvImport.date_format_options?.length
          ? " Additional formats were loaded from the server."
          : ""
      }`;

  const datetimeSelectValue = showCustomDatetimeFormat
    ? DATETIME_FORMAT_CUSTOM_VALUE
    : datetimeFormat || undefined;

  return (
    <div className="flex flex-col gap-5" data-custom-csv-formats-step="">
      {layoutShape ? (
        <Field className="gap-1">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{layoutShape.label}</FieldLabel>
          {layoutShape.description ? (
            <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
              {layoutShape.description}
            </FieldDescription>
          ) : null}
        </Field>
      ) : modeOption ? (
        <Field className="gap-1">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{modeOption.label}</FieldLabel>
          {modeOption.description ? (
            <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
              {modeOption.description}
            </FieldDescription>
          ) : null}
        </Field>
      ) : null}

      {timestampSamples.length > 0 && inferredDatetime ? (
        <Alert
          variant={datetimeFormatConflict ? "warning" : "info"}
          className={BROKER_ALERT_CLASS}
        >
          <BrokerAlertIcon tone={datetimeFormatConflict ? "warning" : "info"} />
          <AlertDescription className={cn(BROKER_ALERT_DESC_CLASS, "space-y-2")}>
            <p className="m-0">
              Timestamps in your file look like{" "}
              <span className={BROKER_ALERT_TITLE_CLASS}>{inferredDatetime.example}</span>
              {datetimeFormat
                ? datetimeFormatConflict
                  ? " — the format you selected does not match."
                  : " — the format you selected matches."
                : " — select that layout below."}
            </p>
            {datetimeFormatConflict || !datetimeFormat ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-font="primary"
                className="h-8 px-4 text-[0.75rem] font-normal normal-case tracking-normal"
                onClick={applyDetectedDatetimeFormat}
              >
                Use{" "}
                {labelForDatetimeFormat(
                  (datetimeFormatConflict ?? inferredDatetime).format,
                  (datetimeFormatConflict ?? inferredDatetime).example
                )}
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card
        data-custom-csv-formats-card=""
        className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
      >
        <CardContent className={CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS}>
          <Field className="gap-2" data-broker-csv-field="">
            <FieldLabel htmlFor="custom-csv-datetime-format" className={BROKER_ALERT_TITLE_CLASS}>
              Date and time format
            </FieldLabel>
            <FieldDescription
              className={cn(
                BROKER_ALERT_DESC_CLASS,
                datetimeFormatIssue && "text-destructive"
              )}
            >
              {datetimeFormatDescription}
            </FieldDescription>
            <Select
              value={datetimeSelectValue}
              onValueChange={(next) => {
                if (next === DATETIME_FORMAT_CUSTOM_VALUE) {
                  setForceCustomDatetimeFormat(true);
                  return;
                }
                setForceCustomDatetimeFormat(false);
                updateTransforms({ datetime_format: next });
              }}
            >
              <SelectTrigger
                id="custom-csv-datetime-format"
                className={CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS}
              >
                <SelectValue placeholder="Select format…" />
              </SelectTrigger>
              <SelectContent {...CUSTOM_CSV_SELECT_CONTENT_PROPS}>
                {datetimeFormatOptions.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
                <SelectItem value={DATETIME_FORMAT_CUSTOM_VALUE}>
                  Other (type format code)…
                </SelectItem>
              </SelectContent>
            </Select>
            {showCustomDatetimeFormat ? (
              <ShadcnInput
                aria-label="Custom date and time format"
                className="font-mono text-sm"
                placeholder="%d-%m-%y %H:%M:%S"
                value={datetimeFormat}
                onChange={(e) => updateTransforms({ datetime_format: e.target.value.trim() })}
              />
            ) : null}
          </Field>
        </CardContent>
      </Card>

      <Card
        data-custom-csv-formats-card=""
        className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
      >
        <CardContent className={CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS}>
          <Field className="gap-2" data-broker-csv-field="">
            <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Decimal separator</FieldLabel>
            <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
              Choose how numbers are written in your file.
            </FieldDescription>
            <RadioGroup
              data-slot="radio-group"
              value={String(state.valueTransforms.decimal_separator ?? ".")}
              onValueChange={(value) => updateTransforms({ decimal_separator: value })}
              className="grid grid-cols-1 gap-2 sm:grid-cols-2"
            >
              {[
                { value: ".", label: "Period (1.25)" },
                { value: ",", label: "Comma (1,25)" },
              ].map((option) => {
                const inputId = `custom-csv-decimal-separator-${option.value}`;
                return (
                  <div key={option.value} className="flex min-w-0 items-start gap-2">
                    <RadioGroupItem
                      value={option.value}
                      id={inputId}
                      data-broker-asset-class-radio=""
                      className="mt-0.5"
                    />
                    <FieldLabel
                      htmlFor={inputId}
                      className={`${BROKER_ALERT_DESC_CLASS} cursor-pointer font-normal`}
                    >
                      {option.label}
                    </FieldLabel>
                  </div>
                );
              })}
            </RadioGroup>
          </Field>
        </CardContent>
      </Card>

      {sideColumn && sideValues.length > 0 && !isTypedTransform(state.valueTransforms.side) ? (
        <MappingSectionCard
          title="Map trade direction values"
          description={
            "Map each direction label in your file to BUY or SELL." +
            (valuesFromFullFile
              ? " Values are scanned from the first 400 rows of your file."
              : "")
          }
        >
          <CsvValueMappingTable
            columnName={sideColumn}
            distinctValues={sideValues}
            targets={SIDE_TARGET_OPTIONS}
            mapping={getValueMap(state.valueTransforms, "side")}
            onChange={(mapping) => updateTransforms({ side: mapping })}
            layout="target-first"
          />
        </MappingSectionCard>
      ) : null}

      {state.layoutShapeId === "fill_fifo" && effectColumn ? (
        <Alert variant="info" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="info" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            This layout does not need an open/close column. {FILE_ORDER_PAIRING_EXPLANATION} Leave{" "}
            <span className={BROKER_ALERT_TITLE_CLASS}>Open / close</span> unmapped on the Map step
            (recommended). If you keep{" "}
            <span className={BROKER_ALERT_TITLE_CLASS}>{effectColumn}</span> mapped, you must map
            its values below.
          </AlertDescription>
        </Alert>
      ) : null}

      {effectColumn && effectColumnIssue ? (
        <Alert variant="warning" className={BROKER_ALERT_CLASS}>
          <BrokerAlertIcon tone="warning" />
          <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
            {effectColumnIssue}
          </AlertDescription>
        </Alert>
      ) : null}

      {effectColumn &&
      effectValuePartition.mappable.length > 0 &&
      !isTypedTransform(state.valueTransforms.position_effect) ? (
        <MappingSectionCard
          title="Map open / close values"
          description="Map each value to OPEN or CLOSE. Several values can mean the same thing. For example, map both MARKET_ORDER_TRADE_CLOSE and MARKET_ORDER_POSITION_CLOSEOUT to CLOSE. Leave deposits and other non-trade rows unmapped."
        >
          <CsvValueMappingTable
            columnName={effectColumn}
            distinctValues={effectValuePartition.mappable}
            ignoredValues={effectValuePartition.ignored}
            targets={POSITION_EFFECT_OPTIONS}
            mapping={getValueMap(state.valueTransforms, "position_effect")}
            onChange={(mapping) => updateTransforms({ position_effect: mapping })}
            layout="source-first"
          />
        </MappingSectionCard>
      ) : null}

      {rowKindColumn &&
      rowKindValuePartition.mappable.length > 0 &&
      !isTypedTransform(state.valueTransforms.row_kind) ? (
        <MappingSectionCard
          title="Map row type values"
          description="Map each value to OPEN or CLOSE. Several values can mean the same thing. Other row types are ignored."
        >
          <CsvValueMappingTable
            columnName={rowKindColumn}
            distinctValues={rowKindValuePartition.mappable}
            ignoredValues={rowKindValuePartition.ignored}
            targets={["OPEN", "CLOSE"]}
            mapping={getValueMap(state.valueTransforms, "row_kind")}
            onChange={(mapping) => updateTransforms({ row_kind: mapping })}
            layout="source-first"
          />
        </MappingSectionCard>
      ) : null}

      {state.layoutShapeId === "fill_fifo" || state.importMode === "execution_rows" ? (
        <Card
          data-custom-csv-formats-card=""
          className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
        >
          <CardContent className={cn(CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS, "flex flex-col gap-4")}>
            <Field className="gap-2" data-broker-csv-field="">
              <FieldLabel
                htmlFor="custom-csv-filter-transaction-type"
                className={BROKER_ALERT_TITLE_CLASS}
              >
                Only import rows where (optional)
              </FieldLabel>
              <ShadcnInput
                id="custom-csv-filter-transaction-type"
                placeholder="e.g. ORDER_FILL"
                value={String(state.valueTransforms.filter_transaction_type ?? "")}
                onChange={(e) => updateTransforms({ filter_transaction_type: e.target.value })}
                className="font-[family-name:var(--font-primary)]"
              />
            </Field>
            <Field className="gap-2" data-broker-csv-field="">
              <FieldLabel
                htmlFor="custom-csv-transaction-type-column"
                className={BROKER_ALERT_TITLE_CLASS}
              >
                Transaction type column header in your file
              </FieldLabel>
              <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
                Enter the exact column name from your CSV that contains transaction types.
              </FieldDescription>
              <ShadcnInput
                id="custom-csv-transaction-type-column"
                placeholder="e.g. TRANSACTION TYPE"
                value={String(state.valueTransforms.transaction_type_column ?? "")}
                onChange={(e) => updateTransforms({ transaction_type_column: e.target.value })}
                className="font-[family-name:var(--font-primary)]"
              />
            </Field>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

export default CustomCsvValueTransformsStep;
